import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { RadioPlayerProvider } from './contexts/RadioPlayerContext';
import { Toaster } from 'sonner';
import './App.css';

import ErrorBoundary from './components/ErrorBoundary';
import { initErrorMonitoring, logApiError } from './services/errorMonitoring';
import axios from 'axios';

import GlobalRadioPlayer from './components/GlobalRadioPlayer';
import RadioTeaserButton from './components/RadioTeaserButton';

initErrorMonitoring();

axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status >= 400) {
      logApiError(error, error.config);
    }
    return Promise.reject(error);
  }
);

// 🔧 VERSÃO ATUAL: CODE SPLITTING - LAZY LOADING com retry para ChunkLoadError
const lazyRetry = (importFn) => lazy(() => 
  importFn().catch(() => {
    // ChunkLoadError após deploy - recarrega a página uma vez
    const reloaded = sessionStorage.getItem('chunk_reload');
    if (!reloaded) {
      sessionStorage.setItem('chunk_reload', '1');
      window.location.reload();
    }
    return importFn();
  })
);

// Páginas públicas
const LandingPage = lazyRetry(() => import('./pages/LandingPage'));
const ChatPage = lazyRetry(() => import('./pages/ChatPage'));
const LoginPage = lazyRetry(() => import('./pages/LoginPage'));
const RegisterPage = lazyRetry(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazyRetry(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazyRetry(() => import('./pages/ResetPasswordPage'));
const ForceChangePasswordPage = lazyRetry(() => import('./pages/ForceChangePasswordPage'));
const VerifyPage = lazyRetry(() => import('./pages/VerifyPage'));
const VerifyEmailPage = lazyRetry(() => import('./pages/VerifyEmailPage'));
const ResendVerificationPage = lazyRetry(() => import('./pages/ResendVerificationPage'));
const TermsPage = lazyRetry(() => import('./pages/TermsPage'));
const GalleryPage = lazyRetry(() => import('./pages/GalleryPage'));
const LyricsGalleryPage = lazyRetry(() => import('./pages/LyricsGalleryPage'));
const LyricDetailPage = lazyRetry(() => import('./pages/LyricDetailPage'));
const TrendingPage = lazyRetry(() => import('./pages/TrendingPage'));
const TestimonialsPage = lazyRetry(() => import('./pages/TestimonialsPage'));
const ArticlesPage = lazyRetry(() => import('./pages/ArticlesPage'));
const ArticleDetailPage = lazyRetry(() => import('./pages/ArticleDetailPage'));
const DiagnosticPage = lazyRetry(() => import('./pages/DiagnosticPage'));
const MusicSharePage = lazyRetry(() => import('./pages/MusicSharePage'));
const PublicWorkPage = lazyRetry(() => import('./pages/PublicWorkPage'));
const CompositorPage = lazyRetry(() => import('./pages/CompositorPage'));
const CompositorShowcasePage = lazyRetry(() => import('./pages/CompositorShowcasePage'));

// Páginas autenticadas
const DashboardPage = lazyRetry(() => import('./pages/DashboardPage'));
const WorksPage = lazyRetry(() => import('./pages/WorksPage'));
const RegisterWorkPage = lazyRetry(() => import('./pages/RegisterWorkPage'));
const WorkDetailPage = lazyRetry(() => import('./pages/WorkDetailPage'));
const EditWorkPage = lazyRetry(() => import('./pages/EditWorkPage'));
const ProfilePage = lazyRetry(() => import('./pages/ProfilePage'));
const CurriculoPage = lazyRetry(() => import('./pages/CurriculoPage'));
const PaymentPage = lazyRetry(() => import('./pages/PaymentPage'));
const PlansPage = lazyRetry(() => import('./pages/PlansPage'));
const BackupManagement = lazyRetry(() => import('./pages/BackupManagement'));

// Páginas admin
const AdminDashboard = lazyRetry(() => import('./pages/AdminDashboard'));
const SystemHealthPage = lazyRetry(() => import('./pages/SystemHealthPage'));
const ISRCManagerPage = lazyRetry(() => import('./pages/ISRCManagerPage'));

// Componentes que carregam sempre (essenciais)
import WhatsAppButton from './components/WhatsAppButton';
import ChatAIWidget from './components/ChatAIWidget';
import GlobalNavbar from './components/GlobalNavbar';
import CPFRequiredModal from './components/CPFRequiredModal';
import WorksNeedAttentionModal from './components/WorksNeedAttentionModal';
import SecuritySeal from './components/SecuritySeal';
import EmailVerificationBanner from './components/EmailVerificationBanner';

// 🔧 VERSÃO ATUAL: Loading Spinner otimizado
const LoadingSpinner = ({ message = "Carregando..." }) => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50">
    <div className="text-center">
      <div className="relative">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-2xl">🎵</span>
        </div>
      </div>
      <p className="mt-4 text-gray-600 font-medium">{message}</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user, sessionError, refreshProfile, clearSessionError, requiresCPFUpdate, cpfUpdateUserInfo, onCPFUpdated } = useAuth();

  if (loading) {
    return <LoadingSpinner message="Verificando autenticação..." />;
  }

  if (isAuthenticated && requiresCPFUpdate) {
    return (
      <CPFRequiredModal 
        isOpen={true}
        onSuccess={onCPFUpdated}
        userName={cpfUpdateUserInfo.name}
        userEmail={cpfUpdateUserInfo.email}
      />
    );
  }

  if (isAuthenticated && !user && sessionError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="text-center max-w-md p-8 bg-white rounded-2xl shadow-lg">
          <div className="text-yellow-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Problema de Conexão</h2>
          <p className="text-gray-600 mb-6">{sessionError}</p>
          <div className="flex flex-col gap-3">
            <button 
              onClick={() => {
                clearSessionError();
                refreshProfile();
              }}
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all"
            >
              🔄 Tentar Novamente
            </button>
            <button 
              onClick={() => window.location.reload()}
              className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
            >
              ↻ Atualizar Página
            </button>
            <a 
              href="/login" 
              className="w-full px-6 py-3 bg-gray-50 text-gray-600 rounded-xl font-semibold hover:bg-gray-100 transition-all text-center"
            >
              Fazer Login Novamente
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (isAuthenticated && user) {
    const userNeedsCPF = !user.cpf_cnpj && !user.is_master;
    
    if (requiresCPFUpdate || userNeedsCPF) {
      return (
        <>
          <CPFRequiredModal 
            isOpen={true}
            onSuccess={onCPFUpdated}
            userName={cpfUpdateUserInfo.name || user.name}
            userEmail={cpfUpdateUserInfo.email || user.email}
          />
          <div className="blur-sm pointer-events-none opacity-30">
            {children}
          </div>
        </>
      );
    }
    return children;
  }
  
  if (isAuthenticated && requiresCPFUpdate) {
    return (
      <CPFRequiredModal 
        isOpen={true}
        onSuccess={onCPFUpdated}
        userName={cpfUpdateUserInfo.name}
        userEmail={cpfUpdateUserInfo.email}
      />
    );
  }

  if (isAuthenticated && !user && !sessionError) {
    return <LoadingSpinner message="Carregando perfil..." />;
  }

  return <Navigate to="/login" replace />;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <LoadingSpinner message="Carregando..." />;
  }

  return (isAuthenticated && user) ? <Navigate to="/dashboard" replace /> : children;
};

const WorksAttentionWrapper = () => {
  const { worksNeedAttention, showWorksAttentionModal, closeWorksAttentionModal, isAuthenticated } = useAuth();
  
  if (!isAuthenticated || !showWorksAttentionModal || worksNeedAttention === 0) {
    return null;
  }
  
  return (
    <WorksNeedAttentionModal 
      isOpen={showWorksAttentionModal}
      onClose={closeWorksAttentionModal}
      worksCount={worksNeedAttention}
    />
  );
};

function AppRoutes() {
  return (
    <>
      <Toaster 
        position="top-right" 
        richColors 
        closeButton
        toastOptions={{
          className: 'font-sans',
          style: {
            background: 'white',
            border: '1px solid #e5e7eb',
          },
        }}
      />
      
      <GlobalNavbar />
      <EmailVerificationBanner />
      <WhatsAppButton />
      <ChatAIWidget />
      
      {/* 🔧 VERSÃO ATUAL: Suspense para lazy loading */}
      <Suspense fallback={<LoadingSpinner message="Carregando página..." />}>
        <Routes>
          {/* Rotas públicas */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/ajuda" element={<ChatPage />} />
          <Route path="/duvidas" element={<ChatPage />} />
          <Route path="/verify/:hash" element={<VerifyPage />} />
          <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
          <Route path="/reenviar-confirmacao" element={<ResendVerificationPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/gallery/lyrics" element={<LyricsGalleryPage />} />
          <Route path="/lyric/:lyricId" element={<LyricDetailPage />} />
          <Route path="/musica/:workId" element={<MusicSharePage />} />
          <Route path="/tendencias" element={<TrendingPage />} />
          <Route path="/depoimentos" element={<TestimonialsPage />} />
          <Route path="/artigos" element={<ArticlesPage />} />
          <Route path="/artigos/:articleId" element={<ArticleDetailPage />} />
          <Route path="/diagnostic" element={<DiagnosticPage />} />
          
          {/* Rotas de autenticação */}
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
          <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
          <Route path="/trocar-senha-obrigatoria" element={<ForceChangePasswordPage />} />

          {/* Rotas protegidas */}
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/works" element={<ProtectedRoute><WorksPage /></ProtectedRoute>} />
          <Route path="/works/new" element={<ProtectedRoute><RegisterWorkPage /></ProtectedRoute>} />
          <Route path="/works/:id" element={<ProtectedRoute><WorkDetailPage /></ProtectedRoute>} />
          <Route path="/works/:id/edit" element={<ProtectedRoute><EditWorkPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/curriculo" element={<ProtectedRoute><CurriculoPage /></ProtectedRoute>} />
          <Route path="/payment/:workId" element={<ProtectedRoute><PaymentPage /></ProtectedRoute>} />
          <Route path="/payment/plan/:planId" element={<ProtectedRoute><PaymentPage /></ProtectedRoute>} />
          <Route path="/plans" element={<ProtectedRoute><PlansPage /></ProtectedRoute>} />
          <Route path="/backups" element={<ProtectedRoute><BackupManagement /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          
          {/* Rotas admin */}
          <Route path="/admin/health" element={<ProtectedRoute><SystemHealthPage /></ProtectedRoute>} />
          <Route path="/isrc-manager" element={<ProtectedRoute><ISRCManagerPage /></ProtectedRoute>} />

          {/* Rotas públicas de obras e compositores */}
          <Route path="/obra/:id" element={<PublicWorkPage />} />
          <Route path="/compositor/:slug" element={<CompositorPage />} />
          <Route path="/compositor-showcase" element={<CompositorShowcasePage />} />
          <Route path="/curriculo-compositor" element={<CompositorShowcasePage />} />
          <Route path="/para-compositores" element={<CompositorShowcasePage />} />

          {/* Rota 404 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary componentName="App">
      <HelmetProvider>
        <AuthProvider>
          <RadioPlayerProvider>
            <BrowserRouter>
              <AppRoutes />
              <SecuritySeal position="bottom-right" showModal={true} />
              <GlobalRadioPlayer />
              <RadioTeaserButton />
              <WorksAttentionWrapper />
            </BrowserRouter>
          </RadioPlayerProvider>
        </AuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
